'use client';

import type { RefObject } from 'react';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { usePulseStore } from '../pulseStore';
import { LifePulse } from '../LifePulse/LifePulse';
import { PulsePopup } from '../PulsePopup/PulsePopup';
import { supportsCssFlip } from './cssFlipSupport';
import flip from './flipTimeline.module.css';
import styles from './StickyPulse.module.css';
import { useStickyRail } from './useStickyRail';
import { useStuck } from './useStuck';

export const StickyPulse = ({
  slotRef,
  play = true,
  ids,
  variant = 'build',
}: {
  slotRef: RefObject<HTMLDivElement | null>;
  play?: boolean;
  ids?: string[] | null;
  variant?: 'build' | 'life';
}) => {
  const railRef = useRef<HTMLDivElement>(null);
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  useStickyRail(railRef, slotRef, mounted);
  const stuck = useStuck(slotRef);
  const hoverIds = usePulseStore((state) => state.hoverIds);
  const activeIds = usePulseStore((state) => state.ids);
  const highlightIds = ids ?? hoverIds ?? activeIds;

  if (!mounted) return null;

  return createPortal(
    <>
      <div
        className={styles.topBlurBar}
        data-visible={stuck}
        aria-hidden={true}
      />

      <div className={styles.rail} ref={railRef}>
        <div className={styles.column}>
          <div className={`${styles.sticky} ${flip.driver}`} data-stuck={stuck}>
            {variant === 'life' ? (
              <LifePulse play={play} />
            ) : (
              <PulsePopup
                play={play}
                ids={highlightIds}
                flipped={supportsCssFlip ? false : stuck}
              />
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};
